import { Inject, Injectable, Injector } from '@angular/core';
import { datadogRum, RumEvent } from '@datadog/browser-rum';
import { MemoryTracker } from '../memory-tracker/memory-tracker';
import { PerformanceInfoProvider } from '../performance-info-provider/performance-info-provider';
import { DataDogConfiguration } from '../../models';
import { RumEventDomainContext } from '@datadog/browser-rum-core/cjs/domainContext.types';

@Injectable()
export class MonitoringInitializerService {
  constructor(
    @Inject('dd-config') private ddConfig: DataDogConfiguration,
    private injector: Injector,
    private performanceInfoProvider: PerformanceInfoProvider
  ) {}

  async initAsync(): Promise<void> {
    if (!this.ddConfig) {
      return;
    }

    const datadogVersion = this.ddConfig.appVersion;
    document.querySelector('body > *').setAttribute('app-version', datadogVersion);

    if (this.ddConfig.disabledMonitoringHosts.includes(location.host)) {
      return;
    }

    datadogRum.init({
      applicationId: this.ddConfig.applicationId,
      clientToken: this.ddConfig.clientToken,
      site: this.ddConfig.site,
      service: this.ddConfig.service,
      version: datadogVersion.trim(),
      allowedTracingUrls: this.ddConfig.allowedTracingOrigins,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      
      trackUserInteractions: true,
      defaultPrivacyLevel: 'mask-user-input',
      traceSampleRate: 100,
      actionNameAttribute: 'monitoring-action-name',
      trackSessionAcrossSubdomains: true,
      trackLongTasks: true,
      env: this.ddConfig.envName,
      beforeSend: (event, context) => this.beforeSend(event, context),
      excludedActivityUrls: [
        'https://api2.amplitude.com',
        'https://api-iam.intercom.io',
        'https://widget.intercom.io',
        'wss://ws-us2.pusher.com',
        /\/entitlements.json$/ // ignore only noisy entitlements.json requests from Stigg SDK
      ]
    });
    datadogRum.startSessionReplayRecording();
    this.startMemoryMonitoring();
    datadogRum.setGlobalContextProperty('screenSize', {
      screenHeight: window.screen.height,
      screenWidth: window.screen.width,
    });
  }

  private beforeSend(rumEvent: RumEvent, context: RumEventDomainContext): boolean {
    const memoryInfo = this.performanceInfoProvider.getMemoryInfo();
    this.enrichClickEventData(rumEvent, context);
    rumEvent.context = { ...rumEvent.context, performanceMemory: memoryInfo };
    return true;
  }

  private startMemoryMonitoring(): void {
    if (typeof this.ddConfig.maxAllowedMemoryUsageMb === 'number') {
      this.injector.get(MemoryTracker).startMemoryTracking();
    }
  }

  private enrichClickEventData(rumEvent: RumEvent, context: RumEventDomainContext): void {
    const isClickEvent = (rumEvent?.action as any)?.type === 'click';
    const clickEventData = {};

    const targetName = (rumEvent?.action as any)?.target?.name;
    if (isClickEvent && targetName) {
      const targetElement = (context as any)?.event?.target as HTMLElement;
      const textContent = targetElement?.textContent?.trim();

      if (textContent) {
        clickEventData['textContent'] = textContent;
      }
    }
    rumEvent.context = { ...rumEvent.context, clickEventData };
  }
}
